import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { isMobile } from 'react-device-detect';
import FeatherIcon from 'feather-icons-react';
import Button from 'components/button';
import Icon from 'components/icons';
import Switch from 'components/switch/Switch';
import Tooltip from 'components/tooltip';
import Notification from 'components/notification';
import { Team } from 'contexts/teams/types';
import { SessionContext } from 'contexts/session';
import { isTeamScopedManager } from 'contexts/session/utilities';
import useLicenseAvailability, { LicenseAvailability } from 'hooks/useLicenseAvailability';
import useSkillDiveToggleState, { skillDiveTooltip } from 'hooks/useSkillDiveToggleState';
import useCanGetSkillDive from 'hooks/useCanGetSkillDive';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import useLicenseRejection from 'hooks/useLicenseRejection';
import { UpdateInviteLinkPayload, getInviteLink, getUpdatedInviteLink } from 'services/inviteLink';
import { INE_URL } from 'constants/domains';
import { CONTENT_TYPES } from 'constants/playlists';
import { LICENSE_REJECTION_OUTCOMES } from 'constants/errors/license_error_codes';
import {
  INVITE_LINK_COPY,
  INVITE_LINK_NOT_CREATED,
  INVITE_LINK_NOT_UPDATED,
  LINK_COPIED,
  inviteLinkDescription,
  managerOfTeamLabel,
  removeChipLabel,
} from 'constants/invites';
import { seatsAvailable } from 'constants/licenses';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import {
  ASSIGN_SKILL_DIVE_LINK_DEFAULT,
  SKILL_DIVE_LEARN_MORE,
  SKILL_DIVE_LEARN_MORE_URL,
  SKILL_DIVE_NOT_ON_PLAN,
  SKILL_DIVE_TOOLTIP_STYLE,
} from 'constants/skill_dive';
import AddByLinkLegacy from './AddByLinkLegacy';
import TeamPicker from './TeamPicker';
import { SelectedTeam } from './types';
import {
  Chip,
  ChipList,
  Container,
  GetSkillDiveLink,
  InviteLinkInput,
  NotOnPlanQualifier,
  OptionalLabel,
  Section,
  SectionDescription,
  SectionSubtitle,
  SectionTitle,
  TeamLinkHeading,
  ToggleLabel,
  ToggleRow,
} from './styles';
import { colors } from 'constants/styles/colors';

// The settings on screen, kept with the payload they produce so a rejected write can put back what
// the link actually has.
interface LinkSettings {
  assignLicense: boolean;
  assignSkillDive: boolean;
  selectedTeams: SelectedTeam[];
  teamRoleManager: boolean;
}

interface LinkWrite {
  key: string;
  payload: Omit<UpdateInviteLinkPayload, 'inviteId'>;
  settings: LinkSettings;
}

interface IAddByLink {
  handleBackPress?: () => void;
  /** Scopes the link to one team: no team picker, and a single "Team role" manager toggle. */
  team?: Team;
}

const availableLabel = ({ mode, value }: LicenseAvailability) =>
  seatsAvailable(mode === 'unlimited' ? '∞' : value);

const chipColor = (team: Team) => team.icon?.color || team.icon_color;

const AddByLinkWithSkillDive: React.FC<IAddByLink> = ({ handleBackPress, team }) => {
  const { licenses_available, teams, account, allows, isTeamManager, isAccountOwner, isStaff } =
    useContext(SessionContext);
  const notifyLicenseRejection = useLicenseRejection();
  const needsTeam = !team && isTeamScopedManager({ isTeamManager, isAccountOwner, isStaff });

  const licenseAvailability = useLicenseAvailability({ count: licenses_available });
  const hasLicensesLeft =
    licenseAvailability.mode === 'unlimited' || (licenseAvailability.value ?? 0) > 0;

  const skillDivePool = account?.account_available_addons?.find(
    (addon) => addon.name === CONTENT_TYPES.SKILL_DIVE
  );
  const skillDiveAvailability = useLicenseAvailability(skillDivePool);
  const skillDiveAssignable =
    skillDiveAvailability.mode === 'unlimited' ||
    (skillDiveAvailability.mode === 'count' && (skillDiveAvailability.value ?? 0) > 0);
  const canGetSkillDive = useCanGetSkillDive(account);

  // Local state is the source of truth for the link's settings. The link response only returns
  // team IDs, without `is_manager`, so nothing is read back from it except the link's id.
  const [assignLicense, setAssignLicense] = useState<boolean>(hasLicensesLeft);
  const [assignSkillDive, setAssignSkillDive] = useState<boolean>(
    ASSIGN_SKILL_DIVE_LINK_DEFAULT && hasLicensesLeft && skillDiveAssignable
  );
  const [selectedTeams, setSelectedTeams] = useState<SelectedTeam[]>([]);
  const [teamRoleManager, setTeamRoleManager] = useState(false);
  const [linkId, setLinkId] = useState<string | null>(null);
  const [saving, setSaving] = useState(!needsTeam);
  // The settings the link has on the server, as a payload key. Copy waits for them.
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [createFailed, setCreateFailed] = useState(false);

  const skillDiveToggle = useSkillDiveToggleState({
    account: { account_available_addons: account?.account_available_addons },
    invitee: { has_license: assignLicense, has_skill_dive: assignSkillDive },
    can_unnasign_license: true,
  });

  const payload: Omit<UpdateInviteLinkPayload, 'inviteId'> = useMemo(
    () => ({
      assign_license: assignLicense,
      assign_skill_dive: assignSkillDive,
      teams: team
        ? [{ team_id: team.id, is_manager: teamRoleManager }]
        : selectedTeams.map((selected) => ({
            team_id: selected.team.id,
            is_manager: selected.is_manager,
          })),
    }),
    [assignLicense, assignSkillDive, team, teamRoleManager, selectedTeams]
  );

  const payloadKey = JSON.stringify(payload);
  const currentSettings: LinkSettings = {
    assignLicense,
    assignSkillDive,
    selectedTeams,
    teamRoleManager,
  };

  // One write at a time: a change made while a write is in flight is sent, as the latest
  // settings, once that write returns. The first write creates the link, the rest update it.
  const latestWrite = useRef<LinkWrite>({ key: payloadKey, payload, settings: currentSettings });
  const savedWrite = useRef<LinkWrite | null>(null);
  const linkIdRef = useRef<string | null>(null);
  const inFlight = useRef(false);
  const dirty = useRef(false);
  const mounted = useRef(true);

  // One render for all four, so no in-between combination gets sent as a write of its own.
  const restoreSettings = (settings: LinkSettings) =>
    unstable_batchedUpdates(() => {
      setAssignLicense(settings.assignLicense);
      setAssignSkillDive(settings.assignSkillDive);
      setSelectedTeams(settings.selectedTeams);
      setTeamRoleManager(settings.teamRoleManager);
    });

  const handleWriteError = (error: unknown, creating: boolean) => {
    notifyLicenseRejection(error, {
      outcome: creating
        ? LICENSE_REJECTION_OUTCOMES.LINK_NOT_CREATED
        : LICENSE_REJECTION_OUTCOMES.LINK_NOT_CHANGED,
      fallback: creating ? INVITE_LINK_NOT_CREATED : INVITE_LINK_NOT_UPDATED,
    });
    if (!mounted.current) return;
    if (creating) {
      // A link that was never created has nothing to put back, so the tab offers a retry.
      setCreateFailed(true);
    } else if (savedWrite.current) {
      // Put back the settings the link still has. That drops the rejected change and any change
      // queued behind it, so the screen never shows settings the link doesn't have.
      restoreSettings(savedWrite.current.settings);
    }
  };

  const writeLink = async () => {
    if (inFlight.current) {
      dirty.current = true;
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setCreateFailed(false);
    let creating = false;
    let failure: { error: unknown } | null = null;
    try {
      // Carries on after the tab closes, so a change made just before closing still reaches the
      // link.
      do {
        dirty.current = false;
        const write = latestWrite.current;
        // Retries and queued changes skip the effect's check.
        if (needsTeam && write.payload.teams.length === 0) break;
        const data = { ...write.payload };
        creating = !linkIdRef.current;
        const response = creating
          ? await getInviteLink(data)
          : await getUpdatedInviteLink({ ...data, inviteId: linkIdRef.current });
        if (response.status >= 200 && response.status < 300 && response.data?.id) {
          linkIdRef.current = response.data.id;
          savedWrite.current = write;
          if (mounted.current) {
            setLinkId(response.data.id);
            setSavedKey(write.key);
          }
        }
      } while (dirty.current);
    } catch (error) {
      failure = { error };
    } finally {
      inFlight.current = false;
      mounted.current && setSaving(false);
    }
    if (failure) handleWriteError(failure.error, creating);
  };

  useEffect(() => {
    latestWrite.current = { key: payloadKey, payload, settings: currentSettings };
    if (needsTeam && payload.teams.length === 0) return;
    // Nothing to send when the settings already match the link, e.g. after a rejected write was
    // put back. A write in flight still needs to hear about it, so it sends these afterwards.
    if (!inFlight.current && savedWrite.current?.key === payloadKey) return;
    writeLink();
  }, [payloadKey]);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const handleLicenseChange = (checked: boolean) => {
    setAssignLicense(checked);
    // Skill Dive needs a training license, so turning training off turns it off too.
    if (!checked) setAssignSkillDive(false);
  };

  const handleTeamToggle = (toggled: Team) => {
    setSelectedTeams((prev) =>
      prev.some((selected) => selected.team.id === toggled.id)
        ? prev.filter((selected) => selected.team.id !== toggled.id)
        : [...prev, { team: toggled, is_manager: false }]
    );
  };

  const handleTeamRemove = (teamId: string) => {
    setSelectedTeams((prev) => prev.filter((selected) => selected.team.id !== teamId));
  };

  const handleManagerToggle = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.map((selected) =>
        selected.team.id === teamId ? { ...selected, is_manager: !selected.is_manager } : selected
      )
    );
  };

  // Sends the settings on screen again, since a link that was never created has none saved.
  const handleRetryCreate = () => writeLink();

  const handleCopyLink = () => {
    const urlElement = document.createElement('input');
    document.body.appendChild(urlElement);
    urlElement.value = `${INE_URL}/invite/${linkId}`;
    urlElement.select();
    document.execCommand('copy');
    document.body.removeChild(urlElement);
    Notification({ text: LINK_COPIED, type: NOTIFICATION_TYPES.SUCCESS });
  };

  const handleLearnMore = () => {
    window.open(SKILL_DIVE_LEARN_MORE_URL, '_blank', 'noopener,noreferrer');
  };

  const skillDiveNotOnPlan = skillDiveAvailability.mode === 'not_on_plan';
  // Copy waits until the link has the settings on screen, so a copied link never grants access
  // that was just turned off.
  const copyDisabled = saving || savedKey !== payloadKey;

  return (
    <>
      {isMobile && (
        <FeatherIcon
          icon={'arrow-left'}
          style={{ position: 'absolute', top: 20, left: 10 }}
          color={colors.fonts.white}
          onClick={handleBackPress}
        />
      )}
      <Container>
        <Section>
          <SectionTitle>{INVITE_LINK_COPY.TITLE}</SectionTitle>
          <SectionDescription>{inviteLinkDescription(Boolean(team))}</SectionDescription>
          <InviteLinkInput>
            <span>
              {linkId && `${INE_URL}/invite/${linkId}`}
              {createFailed && INVITE_LINK_COPY.NOT_CREATED}
            </span>
            {saving && !linkId && (
              <FeatherIcon icon="loader" color={colors.palette.Business_DataVizLilacLighter} />
            )}
            {createFailed ? (
              <Button $secondary onClick={handleRetryCreate}>
                <FeatherIcon
                  icon="refresh-cw"
                  color={colors.palette.Business_DataVizLilacLighter}
                  size={16}
                />{' '}
                {INVITE_LINK_COPY.RETRY}
              </Button>
            ) : (
              <Button $secondary size="small" onClick={handleCopyLink} disabled={copyDisabled}>
                <FeatherIcon
                  icon="copy"
                  color={colors.palette.Business_DataVizLilacLighter}
                  size={16}
                />{' '}
                {INVITE_LINK_COPY.COPY}
              </Button>
            )}
          </InviteLinkInput>
        </Section>

        {team && (
          <Section>
            <TeamLinkHeading>{INVITE_LINK_COPY.TEAM_ROLE}</TeamLinkHeading>
            <ToggleRow>
              <Switch
                aria-label={INVITE_LINK_COPY.TEAM_MANAGER}
                checked={teamRoleManager}
                loading={saving}
                onChange={(checked: boolean) => setTeamRoleManager(checked)}
              />
              <span>{managerOfTeamLabel(team.name)}</span>
            </ToggleRow>
          </Section>
        )}

        <Section>
          {team ? (
            <TeamLinkHeading>{INVITE_LINK_COPY.ASSIGN_ACCESS_TO_NEW_USERS}</TeamLinkHeading>
          ) : (
            <SectionSubtitle>{INVITE_LINK_COPY.ASSIGN_ACCESS}</SectionSubtitle>
          )}
          <ToggleRow>
            <Switch
              aria-label={INVITE_LINK_COPY.TRAINING_LICENSE}
              checked={assignLicense}
              loading={saving}
              disabled={!hasLicensesLeft && !assignLicense}
              onChange={handleLicenseChange}
            />
            <span>
              {INVITE_LINK_COPY.TRAINING_LICENSE} <b>{availableLabel(licenseAvailability)}</b>
            </span>
          </ToggleRow>
          <ToggleRow $alignTop={skillDiveNotOnPlan}>
            {/* Anchored to the toggle's left edge, which sits at the modal's edge. */}
            <Tooltip
              placement="topLeft"
              destroyTooltipOnHide
              overlayInnerStyle={SKILL_DIVE_TOOLTIP_STYLE}
              title={skillDiveTooltip(
                skillDiveToggle,
                Boolean(isStaff || allows?.can_unnasign_license)
              )}
            >
              <span aria-disabled={skillDiveToggle.disabled}>
                <Switch
                  aria-label={INVITE_LINK_COPY.SKILL_DIVE_LICENSE}
                  checked={assignSkillDive}
                  loading={saving}
                  disabled={skillDiveToggle.disabled}
                  onChange={(checked: boolean) => setAssignSkillDive(checked)}
                />
              </span>
            </Tooltip>
            <ToggleLabel>
              <span>
                {INVITE_LINK_COPY.SKILL_DIVE_LICENSE}{' '}
                {skillDiveNotOnPlan ? (
                  <NotOnPlanQualifier>{SKILL_DIVE_NOT_ON_PLAN.QUALIFIER}</NotOnPlanQualifier>
                ) : (
                  <b>{availableLabel(skillDiveAvailability)}</b>
                )}
              </span>
              {/* Only where the plan offers Skill Dive and the account hasn't bought it. */}
              {skillDiveNotOnPlan && canGetSkillDive && (
                <GetSkillDiveLink onClick={handleLearnMore}>
                  {SKILL_DIVE_LEARN_MORE.LINK}
                </GetSkillDiveLink>
              )}
            </ToggleLabel>
          </ToggleRow>
        </Section>

        {!team && (
          <Section>
            <SectionSubtitle>{INVITE_LINK_COPY.INVITE_TO_TEAM}</SectionSubtitle>
            {!needsTeam && <OptionalLabel>{INVITE_LINK_COPY.OPTIONAL}</OptionalLabel>}
            <TeamPicker
              teams={teams || []}
              selectedTeams={selectedTeams}
              saving={saving}
              onToggleTeam={handleTeamToggle}
              onToggleManager={handleManagerToggle}
            />
            {selectedTeams.length > 0 && (
              <ChipList role="list" aria-label={INVITE_LINK_COPY.SELECTED_TEAMS}>
                {selectedTeams.map(({ team: selectedTeam, is_manager }) => (
                  <Chip key={selectedTeam.id} role="listitem" $color={chipColor(selectedTeam)}>
                    <span>
                      {selectedTeam.name}
                      {is_manager && INVITE_LINK_COPY.MANAGER_SUFFIX}
                    </span>
                    <button
                      type="button"
                      aria-label={removeChipLabel(selectedTeam.name)}
                      onClick={() => handleTeamRemove(selectedTeam.id)}
                    >
                      <Icon icon={'Close'} size={'small'} />
                    </button>
                  </Chip>
                ))}
              </ChipList>
            )}
          </Section>
        )}

        <Button onClick={handleCopyLink} disabled={copyDisabled}>
          <FeatherIcon icon="copy" color={colors.palette.Business_DataVizLilacLighter} />{' '}
          {INVITE_LINK_COPY.COPY_INVITE_LINK}
        </Button>
      </Container>
    </>
  );
};

// The new link tab ships behind the Skill Dive invite flag; with it off, the tab stays as it was.
// The team variant only exists in the new tab, whose team modal is behind the same flag.
const AddByLink: React.FC<IAddByLink> = (props) => {
  const skillDiveFlow = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW);
  return skillDiveFlow ? (
    <AddByLinkWithSkillDive {...props} />
  ) : (
    <AddByLinkLegacy handleBackPress={props.handleBackPress} />
  );
};

export default AddByLink;
